import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, TextField, IconButton, Alert, CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { previewImport, executeImport } from '../../api/annotatorImport';
import type { ImportPreview } from '../../types/annotator';

type Step = 'upload' | 'confirm' | 'importing' | 'done';

export default function AnnotatorImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [projectName, setProjectName] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (f: File) => {
    setError('');
    if (!f.name.toLowerCase().endsWith('.zip')) { setError('Please select a .zip file'); return; }
    setFile(f);
    try {
      const p = await previewImport(f);
      setPreview(p);
      setClasses(p.classes);
      const stem = f.name.replace(/\.zip$/i, '').replace(/[_-]/g, ' ');
      setProjectName(stem.charAt(0).toUpperCase() + stem.slice(1));
      setStep('confirm');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to parse ZIP file');
    }
  }, []);

  async function handleExecute(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !projectName.trim()) return;
    const validClasses = classes.filter((c) => c.trim());
    if (validClasses.length === 0) { setError('At least one class is required'); return; }
    setError('');
    setStep('importing');
    try {
      const project = await executeImport(file, projectName.trim(), validClasses, 1);
      setCreatedProjectId(project.id);
      setStep('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Import failed');
      setStep('confirm');
    }
  }

  function handleReset() {
    setStep('upload'); setFile(null); setPreview(null); setProjectName(''); setClasses([]); setError(''); setCreatedProjectId(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>Import YOLO Dataset</Typography>

      {step === 'upload' && (
        <Paper
          sx={{
            p: 6, textAlign: 'center', cursor: 'pointer',
            border: dragOver ? '2px dashed' : '2px dashed transparent',
            borderColor: dragOver ? 'primary.main' : 'divider',
            '&:hover': { borderColor: 'primary.main' },
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          onClick={() => fileRef.current?.click()}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography>Drop a YOLO dataset ZIP here, or click to browse</Typography>
          <Typography variant="caption" color="text.secondary">ZIP should contain: classes.txt, images/, labels/</Typography>
          <input ref={fileRef} type="file" accept=".zip" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} style={{ display: 'none' }} />
        </Paper>
      )}

      {step === 'confirm' && preview && (
        <form onSubmit={handleExecute}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Dataset Summary</Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box><Typography variant="h6">{preview.image_count}</Typography><Typography variant="caption" color="text.secondary">images</Typography></Box>
              <Box><Typography variant="h6">{preview.label_file_count}</Typography><Typography variant="caption" color="text.secondary">label files</Typography></Box>
              <Box><Typography variant="h6">{classes.length}</Typography><Typography variant="caption" color="text.secondary">classes</Typography></Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Project Details</Typography>
            <TextField label="Project Name" value={projectName} onChange={(e) => setProjectName(e.target.value)} required autoFocus fullWidth size="small" sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Classes</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setClasses((p) => [...p, ''])}>Add</Button>
            </Box>
            {classes.map((cls, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 24, textAlign: 'right' }}>{idx}</Typography>
                <TextField size="small" fullWidth value={cls} onChange={(e) => setClasses((p) => p.map((c, i) => i === idx ? e.target.value : c))} placeholder="Class name" />
                <IconButton size="small" onClick={() => setClasses((p) => p.filter((_, i) => i !== idx))}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            ))}
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handleReset}>Back</Button>
            <Button type="submit" variant="contained">Import Dataset</Button>
          </Box>
        </form>
      )}

      {step === 'importing' && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Importing dataset... This may take a moment.</Typography>
        </Paper>
      )}

      {step === 'done' && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>Import Complete</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Project "{projectName}" created with {preview?.image_count} images.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={handleReset}>Import Another</Button>
            {createdProjectId && (
              <Button variant="contained" onClick={() => navigate(`/annotator/projects/${createdProjectId}`)}>Go to Project</Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
